import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './ProgramRotationDetails.module.scss';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import CustomDatePicker from '../../../components/customDatePicker/CustomDatePicker';
import Modal from '../../../components/modal/Modal';

const ProgramRotationDetails = ({ programYears = [], programId, programStartDate, programEndDate, onRefresh }) => {
    const { showToast } = useToasts();
    const { role, can } = useAuth();
    const canUpdateRotationLength = role !== 'Program Coordinator' || can('canUpdateRotationLength');
    const [updates, setUpdates] = useState({});
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [showWarning, setShowWarning] = useState(false);

    // Reset updates if new data comes in
    useEffect(() => {
        setUpdates({});
        setErrors({});
    }, [programYears]);

    const handleValueChange = (yearId, field, value) => {
        setUpdates(prev => ({
            ...prev,
            [yearId]: {
                ...(prev[yearId] || {}),
                [field]: value
            }
        }));

        // Clear error when value changes
        if (errors[`${yearId}_${field}`]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[`${yearId}_${field}`];
                return next;
            });
        }
    };

    const handleIncrement = (yearId, currentVal) => {
        handleValueChange(yearId, 'rotationLength', currentVal + 1);
    };

    const handleDecrement = (yearId, currentVal) => {
        handleValueChange(yearId, 'rotationLength', Math.max(0, currentVal - 1));
    };

    const getDisplayValue = (yearId, field, defaultValue) => {
        if (updates[yearId] && updates[yearId][field] !== undefined) {
            return updates[yearId][field];
        }
        return defaultValue;
    };

    const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${month}-${day}`;
    };

    const hasAnyChanges = () => {
        return Object.keys(updates).length > 0;
    };

    const validateChanges = () => {
        const newErrors = {};
        for (const yearGroup of programYears) {
            const yearId = yearGroup.year.id;
            const startDate = getDisplayValue(yearId, 'rotationStartDate', yearGroup.rotationStartDate ? new Date(yearGroup.rotationStartDate) : null);
            const endDate = getDisplayValue(yearId, 'rotationEndDate', yearGroup.rotationEndDate ? new Date(yearGroup.rotationEndDate) : null);
            if (!startDate) newErrors[`${yearId}_rotationStartDate`] = 'Start Date is required';
            if (!endDate) newErrors[`${yearId}_rotationEndDate`] = 'End Date is required';
        }
        return newErrors;
    };

    const performSave = async () => {
        setIsSaving(true);
        try {
            const rotations = programYears
                .filter(yearGroup => updates[yearGroup.year.id])
                .map(yearGroup => {
                    const yearId = yearGroup.year.id;
                    const yearUpdate = updates[yearId];
                    return {
                        yearId,
                        rotationLength: yearUpdate.rotationLength !== undefined ? yearUpdate.rotationLength : yearGroup.rotationLength,
                        rotationStartDate: yearUpdate.rotationStartDate !== undefined
                            ? formatDate(yearUpdate.rotationStartDate)
                            : yearGroup.rotationStartDate,
                        rotationEndDate: yearUpdate.rotationEndDate !== undefined
                            ? formatDate(yearUpdate.rotationEndDate)
                            : yearGroup.rotationEndDate
                    };
                });

            const response = await axiosClient.patch(`/programs/${programId}/update-rotation-length`, { rotations });
            showToast(response.message, 'success');
            if (onRefresh) onRefresh();
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveAll = () => {
        if (!programId || !hasAnyChanges()) return;

        const newErrors = validateChanges();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setShowWarning(true);
    };

    const handleCancelAll = () => {
        setUpdates({});
        setErrors({});
    };

    const handleSetCurrentYear = async (yearId) => {
        setIsSaving(true);
        try {
            const response = await axiosClient.patch(`/programs/${programId}`, { currentYearId: yearId });
            showToast(response.message, 'success');
            if (onRefresh) onRefresh();
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (!programYears || programYears.length === 0) {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            {isSaving && <LoadingOverlay />}
            
            <div className={styles.commonSection}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>Program Years</h2>
                    {canUpdateRotationLength && hasAnyChanges() && (
                        <div className={styles.saveAction}>
                            <Button 
                                label="Cancel" 
                                variant="primary-light" 
                                size="medium" 
                                onClick={handleCancelAll} 
                            />
                            <Button 
                                label={
                                    <div className={styles.cta}>
                                        <Icon name="save" />
                                        Save Changes
                                    </div>
                                }
                                variant="primary" 
                                size="medium" 
                                onClick={handleSaveAll} 
                            />
                        </div>
                    )}
                </div>

                <div className={styles.yearsGrid}>
                    {programYears.map((yearGroup) => {
                        const yearId = yearGroup.year.id;
                        const rotationLength = getDisplayValue(yearId, 'rotationLength', yearGroup.rotationLength || 0);
                        const startDate = getDisplayValue(yearId, 'rotationStartDate', yearGroup.rotationStartDate ? new Date(yearGroup.rotationStartDate) : null);
                        const endDate = getDisplayValue(yearId, 'rotationEndDate', yearGroup.rotationEndDate ? new Date(yearGroup.rotationEndDate) : null);

                        return (
                            <div key={yearId} className={styles.yearItem}>
                                <div className={styles.yearTitleRow}>
                                    <h3 className={styles.yearTitleSmall}>{yearGroup.year.year}</h3>
                                    {yearGroup.isCurrentYear && (
                                        <div className={styles.currentYearBadge}>
                                            <svg width="15" height="16" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="7.5" cy="7.5" r="7.5" fill="#00B828"/>
                                            <path d="M11.3333 5.5L6.74996 10.0833L4.66663 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span>Current Year</span>
                                        </div>
                                    )}
                                    {yearGroup.canSetAsCurrentYear && (
                                        <button
                                            className={styles.setCurrentYearBtn}
                                            onClick={() => handleSetCurrentYear(yearId)}
                                        >
                                            Set as current Year
                                        </button>
                                    )}
                                </div>
                                
                                <div className={styles.yearContent}>
                                    <div className={styles.dateRow}>
                                        <div className={styles.inputContainer}>
                                            <CustomDatePicker
                                                label="Start Date *"
                                                date={startDate}
                                                onChange={(date) => handleValueChange(yearId, 'rotationStartDate', date)}
                                                showIcon={true}
                                                minDate={programStartDate}
                                                maxDate={endDate || programEndDate}
                                                error={errors[`${yearId}_rotationStartDate`]}
                                                disabled={!canUpdateRotationLength}
                                            />
                                        </div>
                                        <div className={styles.inputContainer}>
                                            <CustomDatePicker
                                                label="End Date *"
                                                date={endDate}
                                                onChange={(date) => handleValueChange(yearId, 'rotationEndDate', date)}
                                                showIcon={true}
                                                minDate={startDate || programStartDate}
                                                maxDate={programEndDate}
                                                error={errors[`${yearId}_rotationEndDate`]}
                                                disabled={!canUpdateRotationLength}
                                            />
                                        </div>
                                    </div>

                                    <div className={styles.rotationLengthRow}>
                                        <div className={styles.inputContainer}>
                                            <span className={styles.inputLabel}>Rotation Length</span>
                                            <div className={styles.inputControls}>
                                                <div className={styles.displayBox}>
                                                    {rotationLength} {rotationLength === 1 ? 'Week' : 'Weeks'}
                                                </div>
                                                <button
                                                    className={styles.minusBtn}
                                                    disabled={rotationLength <= 0 || !canUpdateRotationLength}
                                                    onClick={() => handleDecrement(yearId, rotationLength)}
                                                >
                                                    -
                                                </button>
                                                <button
                                                    className={styles.plusBtn}
                                                    disabled={!canUpdateRotationLength}
                                                    onClick={() => handleIncrement(yearId, rotationLength)}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {showWarning && (
                <Modal scrollable={false}>
                    <div className={styles.warningModal}>
                        <h2 className={styles.warningTitle}>Are you sure you want to save these changes?</h2>
                        <p className={styles.warningMessage}>
                            Changes to Program Years may affect existing schedules associated with this program.
                        </p>
                        <div className={styles.warningActions}>
                            <Button
                                label="Confirm"
                                size="small"
                                variant="secondary"
                                onClick={async () => {
                                    setShowWarning(false);
                                    await performSave();
                                }}
                            />
                            <Button
                                label="Cancel"
                                size="small"
                                variant="cancel"
                                onClick={() => setShowWarning(false)}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

ProgramRotationDetails.propTypes = {
    programYears: PropTypes.arrayOf(PropTypes.shape({
        year: PropTypes.shape({
            id: PropTypes.number,
            year: PropTypes.string
        }),
        rotationLength: PropTypes.number,
        rotationStartDate: PropTypes.string,
        rotationEndDate: PropTypes.string,
        isCurrentYear: PropTypes.bool,
        canSetAsCurrentYear: PropTypes.bool
    })),
    programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    programStartDate: PropTypes.instanceOf(Date),
    programEndDate: PropTypes.instanceOf(Date),
    onRefresh: PropTypes.func
};

export default ProgramRotationDetails;
